import { useMutation, useQuery } from "@tanstack/react-query"

import {
  type AdminFeedbackDetail,
  type AdminFeedbackListResponse,
  type FeedbackStatsResponse,
  getAdminFeedback,
  getAdminFeedbackStats,
  hydrateAdminFeedback,
  listAdminFeedback,
} from "@/api/feedback-api"
import { type Lane } from "@/components/feedback-dialog"

const FEEDBACK_LIST_KEY = ["admin", "feedback", "list"] as const
const feedbackDetailKey = (id: string) => ["admin", "feedback", "detail", id] as const

export function useAdminFeedbackListQuery(params: { lane?: Lane | null; limit?: number } = {}) {
  return useQuery<AdminFeedbackListResponse>({
    queryKey: [...FEEDBACK_LIST_KEY, params.lane ?? null, params.limit ?? null],
    queryFn: () => listAdminFeedback({ lane: params.lane ?? undefined, limit: params.limit }),
  })
}

export function useAdminFeedbackDetailQuery(id: string | null) {
  return useQuery<AdminFeedbackDetail>({
    queryKey: id ? feedbackDetailKey(id) : ["admin", "feedback", "detail", "none"],
    queryFn: () => {
      if (!id) throw new Error("missing id")
      return getAdminFeedback(id)
    },
    enabled: Boolean(id),
  })
}

export function useHydrateAdminFeedbackMutation() {
  return useMutation({
    mutationFn: (id: string) => hydrateAdminFeedback(id),
  })
}

export function useAdminFeedbackStatsQuery(days = 30) {
  return useQuery<FeedbackStatsResponse>({
    queryKey: ["admin", "feedback", "stats", days] as const,
    queryFn: () => getAdminFeedbackStats(days),
  })
}
